<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { sideMenuItems, filterMenuItemsForAccess } from "@/components/sideMenuItems";
import { useCommandPalette } from "@/utils/composables/useCommandPalette";
import { useTheme } from "@/utils/composables/useTheme";
import { hasCapability } from "@/utils/accessControl";

const route = useRoute();
const router = useRouter();
const { isOpen, close } = useCommandPalette();
const { setTheme } = useTheme();

const userDetails = JSON.parse(localStorage.getItem("user") || "null");
const superAdminEmail = process.env.VUE_APP_SUPER_ADMIN_EMAIL;

const query = ref("");
const activeIndex = ref(0);
const inputRef = ref(null);
const listRef = ref(null);

function flattenMenu(items, group = []) {
  const result = [];
  for (const item of items) {
    const children = item.subItems || item.children || [];
    if (children.length) {
      result.push(...flattenMenu(children, [...group, item.label]));
    } else if (item.link) {
      result.push({
        id: `nav:${item.link}`,
        label: item.label,
        group: group.join(" › ") || "Navigate",
        icon: item.svg,
        run: () => router.push(item.link),
      });
    }
  }
  return result;
}

const navEntries = computed(() =>
  flattenMenu(filterMenuItemsForAccess(sideMenuItems, userDetails, superAdminEmail)),
);

const actionEntries = computed(() => [
  {
    id: "action:campaign-new",
    label: "New Campaign",
    group: "Actions",
    capability: "campaigns",
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>',
    run: () => router.push("/campaigns/create"),
  },
  {
    id: "action:theme-light",
    label: "Switch to light theme",
    group: "Actions",
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>',
    run: () => setTheme("light"),
  },
  {
    id: "action:theme-dark",
    label: "Switch to dark theme",
    group: "Actions",
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"/></svg>',
    run: () => setTheme("dark"),
  },
  {
    id: "action:theme-system",
    label: "Match system theme",
    group: "Actions",
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><path d="M8 21h8M12 17v4"/></svg>',
    run: () => setTheme("system"),
  },
].filter((entry) => !entry.capability || hasCapability(entry.capability)));

const allEntries = computed(() => [...navEntries.value, ...actionEntries.value]);

const filteredEntries = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return allEntries.value;
  return allEntries.value.filter((entry) =>
    `${entry.label} ${entry.group}`.toLowerCase().includes(q),
  );
});

watch(filteredEntries, () => {
  activeIndex.value = 0;
});

watch(isOpen, async (open) => {
  if (open) {
    query.value = "";
    activeIndex.value = 0;
    await nextTick();
    inputRef.value?.focus();
  }
});

watch(
  () => route.fullPath,
  () => close(),
);

function selectEntry(entry) {
  if (!entry) return;
  close();
  entry.run();
}

function onKeydown(e) {
  if (e.key === "Escape") {
    e.preventDefault();
    close();
  } else if (e.key === "ArrowDown") {
    e.preventDefault();
    activeIndex.value = Math.min(activeIndex.value + 1, filteredEntries.value.length - 1);
    scrollActiveIntoView();
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    activeIndex.value = Math.max(activeIndex.value - 1, 0);
    scrollActiveIntoView();
  } else if (e.key === "Enter") {
    e.preventDefault();
    selectEntry(filteredEntries.value[activeIndex.value]);
  }
}

async function scrollActiveIntoView() {
  await nextTick();
  const el = listRef.value?.querySelector(".cmdk-item.is-active");
  el?.scrollIntoView({ block: "nearest" });
}
</script>

<template>
  <Teleport to="body">
    <div v-if="isOpen" class="cmdk-overlay" @mousedown.self="close">
      <div class="cmdk-panel" role="dialog" aria-modal="true" aria-label="Command palette">
        <div class="cmdk-search">
          <i class="bi bi-search cmdk-search-icon"></i>
          <input
            ref="inputRef"
            v-model="query"
            type="text"
            class="cmdk-input"
            placeholder="Search pages and actions…"
            autocomplete="off"
            spellcheck="false"
            @keydown="onKeydown"
          />
          <span class="cmdk-kbd">Esc</span>
        </div>

        <div ref="listRef" class="cmdk-list">
          <div v-if="!filteredEntries.length" class="cmdk-empty">No matches for "{{ query }}"</div>
          <button
            v-for="(entry, index) in filteredEntries"
            :key="entry.id"
            type="button"
            class="cmdk-item"
            :class="{ 'is-active': index === activeIndex }"
            @mouseenter="activeIndex = index"
            @click="selectEntry(entry)"
          >
            <span class="cmdk-item-icon" v-html="entry.icon"></span>
            <span class="cmdk-item-label">{{ entry.label }}</span>
            <span class="cmdk-item-group">{{ entry.group }}</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.cmdk-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12vh;
  background: rgba(15, 23, 42, 0.35);
  animation: cmdk-fade 0.15s ease-out;
}

.cmdk-panel {
  width: 100%;
  max-width: 560px;
  max-height: 70vh;
  margin: 0 16px;
  background: var(--neutral-0);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.35), 0 4px 16px -4px rgba(15, 23, 42, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: cmdk-rise 0.15s ease-out;
}

@keyframes cmdk-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes cmdk-rise {
  from { opacity: 0; transform: translateY(-8px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.cmdk-search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--neutral-200);
  flex-shrink: 0;
}

.cmdk-search-icon {
  color: var(--neutral-400);
  font-size: 0.95rem;
}

.cmdk-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  color: var(--neutral-900);
}

.cmdk-input::placeholder {
  color: var(--neutral-400);
}

.cmdk-kbd {
  font-size: 11px;
  font-weight: 600;
  color: var(--neutral-500);
  background: var(--neutral-100);
  border-radius: 6px;
  padding: 3px 6px;
  flex-shrink: 0;
}

.cmdk-list {
  overflow-y: auto;
  padding: 8px;
}

.cmdk-empty {
  padding: 24px 12px;
  text-align: center;
  font-size: 13px;
  color: var(--neutral-400);
}

.cmdk-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.1s ease;
}

.cmdk-item.is-active {
  background: var(--neutral-100);
}

.cmdk-item-icon {
  display: inline-flex;
  color: var(--neutral-500);
  flex-shrink: 0;
}

.cmdk-item-icon :deep(svg) {
  width: 17px;
  height: 17px;
}

.cmdk-item.is-active .cmdk-item-icon {
  color: var(--primary-color);
}

.cmdk-item-label {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--neutral-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cmdk-item-group {
  font-size: 12px;
  color: var(--neutral-400);
  flex-shrink: 0;
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .cmdk-overlay {
  background: rgba(0, 0, 0, 0.55);
}
</style>
